import * as React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { useAuth } from '@/hooks/useAuth';
import { VersionPill } from '@/components/ui/version-pill';
import { useToast } from '@/components/ui/use-toast';

export function AccessRequest() {
	const { logout, user: web3AuthUser } = useWeb3Auth();
	const { checkUserInDatabase } = useAuth();
	const navigate = useNavigate();
	const { toast } = useToast();
	const [isChecking, setIsChecking] = React.useState(false);

	const handleLogout = async () => {
		await logout();
		navigate('/');
	};

	const checkUserRecord = React.useCallback(async () => {
		setIsChecking(true);
		try {
			console.log(
				'Checking user record manually with email:',
				web3AuthUser?.email
			);
			const dbUser = await checkUserInDatabase();

			console.log('Manual database check result:', {
				user: dbUser,
				found: !!dbUser,
				role: dbUser?.role,
			});

			if (dbUser) {
				// User exists, show success message
				toast({
					title: 'Database Check Result',
					description: `Found user record for ${web3AuthUser?.email}`,
					duration: 3000,
				});

				// Redirect to appropriate dashboard
				const isAdmin =
					dbUser.role === 'admin' || dbUser.role === 'superadmin';
				setTimeout(() => {
					navigate(isAdmin ? '/admin' : '/user');
				}, 1000);
			} else {
				toast({
					title: 'Database Check Result',
					description: `No user record found for ${web3AuthUser?.email}`,
					duration: 3000,
				});
			}
		} catch (error) {
			console.error('Error checking user record:', error);
			toast({
				title: 'Error',
				description: 'Failed to check user record',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsChecking(false);
		}
	}, [web3AuthUser?.email, checkUserInDatabase, toast, navigate]);

	React.useEffect(() => {
		// Auto-check on mount
		const autoCheck = async () => {
			await checkUserRecord();
		};
		autoCheck();
	}, [checkUserRecord]);

	return (
		<div className='relative min-h-screen'>
			<div className='orb-container'>
				<div className='orb orb-1'></div>
				<div className='orb orb-2'></div>
				<div className='orb orb-3'></div>
				<div className='orb orb-4'></div>
				<div className='orb orb-5'></div>
			</div>
			<div className='absolute inset-0 bg-black/40 z-0' />
			<main className='relative z-10'>
				<div className='container mx-auto px-4 py-20'>
					<div className='flex flex-col items-center justify-center space-y-12'>
						<div className='w-full max-w-3xl mx-auto text-center glass-morphism p-8 rounded-lg'>
							<div className='flex flex-col items-center space-y-6'>
								<img
									src='/logo.png'
									alt='Prometheus'
									className='w-[300px] h-auto'
								/>

								<div className='max-w-xl mx-auto'>
									<h1 className='text-2xl font-bold text-orange-500 mb-4'>
										Access Request Pending
									</h1>

									<div className='p-6 border border-orange-500/30 rounded-lg bg-black/40 mb-6'>
										<p className='text-lg mb-4'>
											Thank you for logging in,{' '}
											{web3AuthUser?.email || 'user'}.
										</p>
										<p className='text-lg mb-4'>
											Your account requires administrator
											approval before you can access the
											platform.
										</p>
										<p className='text-lg mb-4'>
											Please contact your administrator to
											request access.
										</p>
									</div>

									<div className='flex flex-col space-y-4'>
										<Button
											size='lg'
											onClick={handleLogout}
											className='bg-gradient-to-b from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-12 py-6 h-auto'
										>
											<span className='text-xl font-semibold'>
												RETURN TO LOGIN
											</span>
										</Button>

										<Button
											variant='outline'
											size='lg'
											onClick={checkUserRecord}
											disabled={isChecking}
											className='border border-orange-500/50 hover:bg-orange-500/10 text-orange-500 px-12 py-6 h-auto'
										>
											<span className='text-xl font-semibold'>
												{isChecking
													? 'CHECKING...'
													: 'CHECK DATABASE RECORD'}
											</span>
										</Button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
			<VersionPill />
		</div>
	);
}
